import { ButtonVariant, CoButton } from "components/CoButton";
import EmptyModal from "components/Modal/EmptyModal";
import { PdfAccessTag } from "components/PdfAccessTag";
import LibrarySelectBox from "features/libraries/components/LibrarySelectBox";
import { Library } from "helpers/api";
import useLabels from "hooks/useLabels";
import { useEffect, useState } from "react";

const PDF_TAGS = [
  {
    rotation: -1,
    logo: "/images/university-mit.png",
    alt: "Massachusetts Institute of Technology",
  },
  {
    rotation: 3,
    logo: "/images/university-michigan.png",
    alt: "University of Michigan",
  },
  {
    rotation: -1,
    logo: "/images/university-stanford.png",
    alt: "Stanford University",
  },
  {
    rotation: -2,
    logo: "/images/university-texas-a&m.png",
    alt: "Texas A&M University",
  },
];

type InstitutionSelectionModalProps = {
  open: boolean;
  onConfirm: (selectedLibrary: Library | null) => void;
  onCancel: () => void;
  matchingLibraries: Library[];
};

const InstitutionSelectionModal: React.FC<InstitutionSelectionModalProps> = ({
  open,
  onConfirm,
  onCancel,
  matchingLibraries,
}) => {
  const [labels] = useLabels("screens.institution-selection");
  const [selectedLibrary, setSelectedLibrary] = useState<Library | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<string>("");

  useEffect(() => {
    if (matchingLibraries.length > 0 && selectedLibrary === null) {
      const firstLibrary = matchingLibraries[0];
      setSelectedLibrary(firstLibrary);
      if ("matched_email" in firstLibrary) {
        setSelectedEmail(firstLibrary.matched_email as string);
      }
    }
  }, [matchingLibraries, selectedLibrary]);

  if (!open) return null;

  return (
    <EmptyModal open={open} onClose={onCancel}>
      <div className="w-full sm:max-w-[500px] mx-auto h-full bg-bg-base shadow-elevation overflow-hidden rounded-2xl flex flex-col">
        {/* Header with background pattern */}
        <div
          className="relative pb-8 border-b border-border-base flex flex-col items-center"
          style={{
            backgroundImage: "url('/images/parcel-paper.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#F6FAFF", // Custom color for background pattern
          }}
        >
          {/* Close button */}
          <div className="self-stretch pt-3 pb-3">
            <div className="px-4 pr-1.5 flex justify-end">
              <CoButton
                variant={ButtonVariant.Flat}
                size="sm"
                onClick={onCancel}
                icon="icon-x"
                data-testid="institution-modal-close-button"
              />
            </div>
          </div>

          {/* PDF Tags */}
          <div className="flex justify-center items-center gap-3 mb-4">
            {PDF_TAGS.map((tag, index) => (
              <PdfAccessTag
                key={index}
                icon={tag.logo}
                text={labels["pdf"]}
                rotation={tag.rotation}
                alt={tag.alt}
                className="bg-bg-subtle text-fg-muted"
              />
            ))}
          </div>

          {/* Title */}
          <div className="w-full px-4 text-center">
            <h2 className="text-fg-base h2-base">{labels["title"]}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-hidden flex flex-col gap-6">
          <p className="text-fg-base text-base">{labels["description"]}</p>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-fg-base sm-md">
                {labels["your-institution"]}
              </label>
              {selectedEmail && (
                <p className="text-fg-muted sm-normal">
                  {labels["auto-selected-from-email"]?.replace(
                    "{userEmailDomain}",
                    selectedEmail.split("@")[1]
                  )}
                </p>
              )}
            </div>

            {/* Library selection dropdown */}
            <LibrarySelectBox
              value={selectedLibrary}
              onChange={(library) => {
                setSelectedLibrary(library);
                // Clear the selected email when library is cleared
                if (!library) {
                  setSelectedEmail("");
                }
              }}
              mode="inside-modal"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-border-base flex flex-col-reverse gap-2 items-stretch sm:flex-row sm:justify-end sm:items-center">
          <CoButton
            variant={ButtonVariant.Tertiary}
            size="sm"
            onClick={onCancel}
            label={labels["cancel"]}
            data-testid="institution-modal-cancel-button"
            className="w-full sm:w-auto"
          />
          <CoButton
            variant={ButtonVariant.Primary}
            size="sm"
            onClick={() => onConfirm(selectedLibrary)}
            disabled={!selectedLibrary}
            label={labels["confirm"]}
            data-testid="institution-modal-confirm-button"
            className="w-full sm:w-auto"
          />
        </div>
      </div>
    </EmptyModal>
  );
};

export default InstitutionSelectionModal;
